import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CardCustomization } from '@anidex/shared/economy';
import type { CardCustomizationInput } from '@anidex/shared/validation';
import type {
  BulkUpgradeResultDTO,
  CharacterDetailDTO,
  FragmentConversionResultDTO,
  OwnedCardDTO,
  OwnedCharacterIdsDTO,
  Page,
  StashedFragmentsDTO,
  UpgradeResultDTO,
} from '@anidex/shared/types';
import { CARD_PAGE_SIZE, pageCursor } from '../../hooks/useListPage';
import { api, queryString } from '../api';
import { hasListIntent } from '../list-intent';
import { useSession } from './account';
import { invalidateKeys, isCollectionListQuery, refreshProgress } from './cache';
import type { CollectionFilters } from './filters';
import { keys } from './keys';

export function useCollection(filters: CollectionFilters, enabled = true) {
  const page = filters.page ?? 0;
  const limit = filters.limit ?? CARD_PAGE_SIZE;
  const intent = hasListIntent(filters);
  return useQuery({
    queryKey: keys.collection(filters),
    enabled: enabled && intent,
    queryFn: () =>
      api<Page<OwnedCardDTO>>(
        `/collection${queryString({
          search: filters.search,
          rarity: filters.rarity,
          nametag: filters.nametag,
          animeId: filters.animeId,
          favorites: filters.favorites ? '1' : undefined,
          available: filters.available ? '1' : undefined,
          tradeId: filters.tradeId,
          sort: filters.sort,
          limit,
          cursor: pageCursor(page, limit),
        })}`,
      ),
    placeholderData: intent ? keepPreviousData : undefined,
  });
}


function toOwnedSet(data: OwnedCharacterIdsDTO) {
  return new Set(data.ids);
}


export function useOwnedCharacters() {
  const loggedIn = Boolean(useSession().data?.user);
  return useQuery({
    queryKey: keys.ownedIds,
    queryFn: () => api<OwnedCharacterIdsDTO>('/collection/ids'),
    enabled: loggedIn,
    staleTime: 5 * 60_000,
    select: toOwnedSet,
  });
}


export function useStashedFragments(enabled = true) {
  return useQuery({
    queryKey: keys.stashedFragments,
    enabled,
    queryFn: () => api<StashedFragmentsDTO>('/collection/fragments'),
  });
}


export function useBulkUpgrade() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { characterIds?: number[]; all?: boolean; afterCharacterId?: number }) =>
      api<BulkUpgradeResultDTO>('/characters/upgrade', { body: input }),
    onSuccess: (result) => {
      if (result.upgraded > 0) {
        refreshProgress(client);
        invalidateKeys(client, [['public_profile'], ['public_collection'], ['compare_cards'], ['atlas'], ['partner_cards']]);
      }
    },
  });
}


export function useConvertFragments() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api<FragmentConversionResultDTO>('/characters/fragments/convert', { body: {} }),
    onSuccess: (result) => {
      if (result.converted > 0) refreshProgress(client, result.balance);
    },
  });
}

export function useCharacter(id: number | null) {
  return useQuery({
    queryKey: keys.character(id ?? 0),
    queryFn: () => api<CharacterDetailDTO>(`/characters/${id}`),
    enabled: id !== null,
  });
}

export function useUpgrade() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (characterId: number) => api<UpgradeResultDTO>(`/characters/${characterId}/upgrade`, { body: {} }),
    onSuccess: async (result, characterId) => {
      await client.cancelQueries({ queryKey: keys.character(characterId), exact: true });
      client.setQueryData<CharacterDetailDTO>(keys.character(characterId), (detail) =>
        detail ? { ...detail, ownership: result.ownership } : detail,
      );
      refreshProgress(client);
      invalidateKeys(client, [['public_profile'], ['public_collection'], ['compare_cards'], ['atlas'], ['partner_cards']]);
    },
  });
}

export function useToggleFavorite() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ characterId, favorite }: { characterId: number; favorite: boolean }) =>
      api<{ isFavorite: boolean }>(`/characters/${characterId}/favorite`, { method: 'PUT', body: { favorite } }),
    onSuccess: (_result, { characterId }) => {
      invalidateKeys(client, [keys.character(characterId), ['anime']]);
      void client.invalidateQueries({ predicate: isCollectionListQuery });
    },
  });
}

export function useToggleWish() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ characterId, wished }: { characterId: number; wished: boolean }) =>
      api<{ isWished: boolean }>(`/characters/${characterId}/wish`, { method: 'PUT', body: { wished } }),
    onSuccess: (_result, { characterId }) => {
      invalidateKeys(client, [keys.character(characterId), ['anime']]);
      void client.invalidateQueries({
        predicate: (query) => isCollectionListQuery(query) || query.queryKey[0] === 'public_collection',
      });
    },
  });
}

export function useSetCardNametag() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ characterId, nametag }: { characterId: number; nametag: string | null }) =>
      api<{ nametag: string | null }>(`/characters/${characterId}/nametag`, { method: 'PUT', body: { nametag } }),
    onSuccess: (_result, { characterId }) => {
      invalidateKeys(client, [
        ['card_nametags'],
        keys.character(characterId),
        ['public_collection'],
        ['public_profile'],
        ['compare_cards'],
        ['atlas'],
        ['partner_cards'],
        ['trades'],
        ['trade'],
        ['market'],
        keys.marketMine,
        keys.marketHistory,
        ['auction'],
        ['auctions'],
        keys.auctionsMine,
        ['message'],
      ]);
      void client.invalidateQueries({ predicate: isCollectionListQuery });
    },
  });
}

export function useSetCardCustomization() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ characterId, customization }: { characterId: number; customization: CardCustomizationInput }) =>
      api<{ customization: CardCustomization }>(`/characters/${characterId}/customization`, { method: 'PUT', body: customization }),
    onSuccess: async (result, { characterId }) => {
      await client.cancelQueries({ queryKey: keys.character(characterId), exact: true });
      client.setQueryData<CharacterDetailDTO>(keys.character(characterId), (detail) =>
        detail?.ownership ? { ...detail, ownership: { ...detail.ownership, customization: result.customization } } : detail,
      );
      invalidateKeys(client, [
        keys.character(characterId), ['anime'], ['public_collection'], ['public_profile'],
        ['compare_cards'], ['atlas'], ['partner_cards'], ['trades'], ['trade'],
        ['market'], keys.marketMine, ['auction'], ['auctions'], keys.auctionsMine,
      ]);
      void client.invalidateQueries({ predicate: isCollectionListQuery });
    },
  });
}

export function useCardNametags(prefix: string) {
  const userId = useSession().data?.user?.id;
  return useQuery({
    queryKey: ['card_nametags', userId, prefix],
    queryFn: () => api<{ items: string[] }>(`/collection/nametags${queryString({ q: prefix })}`),
    enabled: Boolean(userId) && prefix.length > 0 && prefix.length <= 32,
    staleTime: 5 * 60_000,
  });
}
